import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { List, Section, Cell, Caption, Spinner } from "@telegram-apps/telegram-ui";
import { authWithWidget, getSession } from "../lib/api.js";

// Telegram Login Widget bootstrap:
//   - We fetch the bot username from /api/v1/auth/config so the script tag
//     can target the right bot without baking it into the build.
//   - The widget calls window.<callback>(user) with the signed payload;
//     we POST it to our backend, which validates the hash and returns a
//     session token identical to the Mini App auth flow.
//   - BotFather requirement: /setdomain rcflight.печкры.рф — without this,
//     the widget refuses to render. We surface that hint on failure.

export default function Login() {
  const navigate = useNavigate();
  const containerRef = useRef(null);
  const [botUsername, setBotUsername] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (getSession()) {
      navigate("/", { replace: true });
      return;
    }
    fetch("/api/v1/auth/config")
      .then((r) => r.json())
      .then((cfg) => {
        if (!cfg?.bot_username) {
          setError("Сервер не сообщил имя бота — проверьте TELEGRAM_BOT_USERNAME.");
          return;
        }
        setBotUsername(cfg.bot_username);
      })
      .catch((e) => setError(`Не удалось получить конфиг: ${e.message}`));
  }, [navigate]);

  useEffect(() => {
    if (!botUsername || !containerRef.current) return;

    const callbackName = `__rcfTelegramAuth_${Math.random().toString(36).slice(2)}`;
    window[callbackName] = async (user) => {
      setBusy(true);
      setError(null);
      try {
        await authWithWidget(user);
        navigate("/", { replace: true });
      } catch (e) {
        setError(`Авторизация отклонена: ${e.message}`);
        setBusy(false);
      }
    };

    const script = document.createElement("script");
    script.src = "https://telegram.org/js/telegram-widget.js?22";
    script.async = true;
    script.setAttribute("data-telegram-login", botUsername);
    script.setAttribute("data-size", "large");
    script.setAttribute("data-onauth", `${callbackName}(user)`);
    script.setAttribute("data-request-access", "write");
    script.onerror = () => setError(
      "Не удалось загрузить виджет Telegram. Проверьте, что домен зарегистрирован в BotFather (/setdomain)."
    );
    containerRef.current.appendChild(script);

    return () => {
      delete window[callbackName];
      try { containerRef.current?.removeChild(script); } catch {}
    };
  }, [botUsername, navigate]);

  return (
    <List>
      <Section>
        <Cell multiline>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8, padding: "20px 12px 4px", textAlign: "center" }}>
            <div style={{ fontSize: 42 }}>🛩</div>
            <div className="rcf-hero__eyebrow">RCFlyers</div>
            <div style={{ fontSize: 22, fontWeight: 700, letterSpacing: "-0.01em" }}>Войдите через Telegram</div>
            <div className="rcf-hero__sub" style={{ maxWidth: 320 }}>
              Сессия сохранится в этом браузере — повторно подтверждать не нужно.
            </div>
            <Link to="/welcome" style={{ fontSize: 13, color: "var(--portal-accent, #2563eb)", textDecoration: "none", marginTop: 4 }}>
              Что это за сервис?
            </Link>
          </div>
        </Cell>
      </Section>

      <Section>
        <div style={{ padding: "20px 16px", display: "flex", justifyContent: "center", minHeight: 72 }}>
          {!botUsername && !error && <Spinner size="m" />}
          <div ref={containerRef} />
          {busy && <Spinner size="m" />}
        </div>
        {error && (
          <Cell multiline>
            <Caption level="1" style={{ color: "var(--tgui--destructive_text_color, #d33)" }}>
              {error}
            </Caption>
          </Cell>
        )}
      </Section>
    </List>
  );
}
